
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blog';
import { blogContent } from '../../../mocks/blogContent';
import { useSEO, generateArticleSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const postId = parseInt(id || '1');
  const post = blogPosts.find(p => p.id === postId);
  const content = blogContent[postId];

  const wordCount = content ? content.content.join(' ').split(/\s+/).length : 0;

  const schemas = post
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          generateArticleSchema(
            post.title,
            post.excerpt,
            `/blog/${postId}`,
            post.date,
            post.date,
            post.author,
            post.image,
            post.category,
            wordCount
          ),
          generateBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: post.title, url: `/blog/${postId}` },
          ]),
        ],
      }
    : undefined;

  useSEO({
    title: post ? `${post.title} | Roseville.org Blog` : 'Blog | Roseville.org',
    description: post?.excerpt || 'Read the latest articles and stories from Roseville, California.',
    keywords: post
      ? `${post.category}, Roseville blog, ${post.title}, Roseville California`
      : 'Roseville blog, Roseville California',
    canonical: `/blog/${postId}`,
    ogType: 'article',
    ogImage: post?.image,
    ogImageAlt: post?.title,
    author: post?.author,
    articlePublishedTime: post?.date,
    articleModifiedTime: post?.date,
    schema: schemas,
  });

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Article not found.</p>
          <Link to="/blog" className="text-teal-600 font-semibold hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[420px] w-full overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-4xl mx-auto">
          <span className="inline-block bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {post.readTime}
            </span>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-700 font-medium truncate max-w-xs">{post.title}</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Article Body */}
          <div className="lg:col-span-2">
            <p className="text-lg text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-teal-500 pl-5">
              {post.excerpt}
            </p>

            {content ? (
              <div className="prose prose-gray max-w-none">
                {content.content.map((paragraph, idx) => (
                  <p key={idx} className="text-gray-700 leading-relaxed mb-5 text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">Full article content coming soon.</p>
            )}

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {[post.category, 'Roseville', 'California', 'Community'].map(tag => (
                  <span
                    key={tag}
                    className="bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Author Card */}
            <div className="mt-8 bg-gray-50 rounded-2xl p-6 flex items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                <i className="ri-user-3-line text-teal-600 text-2xl"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{post.author}</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  Contributing writer at Roseville.org — covering local stories, events, and community life.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Back to Blog */}
            <Link
              to="/blog"
              className="flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Back to All Articles
            </Link>

            {/* Article Info */}
            <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
              <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">
                Article Info
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-folder-line text-teal-600"></i>
                  </div>
                  <span>{post.category}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-calendar-line text-teal-600"></i>
                  </div>
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-time-line text-teal-600"></i>
                  </div>
                  <span>{post.readTime}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-file-word-line text-teal-600"></i>
                  </div>
                  <span>{wordCount} words</span>
                </div>
              </div>
            </div>

            {/* More Articles */}
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-4">
                More Articles
              </h3>
              <div className="space-y-4">
                {blogPosts
                  .filter(p => p.id !== postId)
                  .slice(0, 4)
                  .map(related => (
                    <Link
                      key={related.id}
                      to={`/blog/${related.id}`}
                      className="flex gap-3 group"
                    >
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-16 h-12 object-cover object-top rounded-lg flex-shrink-0"
                      />
                      <div>
                        <p className="text-xs font-medium text-gray-800 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
                          {related.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{related.readTime}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
